import type {
  InitialData as StopFormInitialData,
  ValidData as StopFormValidData,
} from '@/components/forms/JobEditStopForm.vue';
import type { VFormInstance } from '@/composables/useForm';
import type { StopItem } from '@/models/jobEdit';

import { ref } from 'vue';
import cloneDeep from 'lodash/cloneDeep';

export interface StopFormRef {
  readonly form: VFormInstance | null;
  buildSubmitData: () => StopFormValidData;
  resetData: (data: StopFormInitialData) => void;
}

export function useJobEditStop(initialStop: () => StopItem) {
  const formRef = ref<StopFormRef | null>(null);

  function setFormRef(el: StopFormRef | null): void {
    formRef.value = el;
  }

  function getInitialData(): StopFormInitialData {
    const stop = cloneDeep(initialStop());
    return {
      stop_number: stop.stop_number,
      name: stop.name,
      address_line_1: stop.address_line_1,
      address_line_2: stop.address_line_2,
      suburb: stop.suburb,
      post_code: stop.post_code,
      state: stop.state,
      time_earliest: stop.time_earliest,
      time_latest: stop.time_latest,
      note_name: stop.note_name,
      note_phone: stop.note_phone,
      note_text: stop.note_text,
      duration: stop.duration,
      attributes: stop.attributes,
      is_warehouse: stop.is_warehouse,
      driver_is: stop.driver_is,
      arrived_at: stop.arrived_at,
      departed_at: stop.departed_at,
      schedule_start: stop.schedule_start,
      schedule_finish: stop.schedule_finish,
      pod_time: stop.pod_time,
    };
  }

  const initialData = ref<StopFormInitialData>(getInitialData());

  function refreshInitialData(): void {
    const freshData = getInitialData();
    initialData.value = freshData;
    formRef.value?.resetData(freshData);
    formRef.value?.form?.resetValidation();
  }

  async function validate(): Promise<boolean> {
    const form = formRef.value?.form;
    if (!form) return false;
    const { valid } = await form.validate();
    return valid;
  }

  function getFormData(): StopFormValidData | null {
    if (!formRef.value) return null;
    return formRef.value.buildSubmitData();
  }

  return {
    setFormRef,
    refreshInitialData,
    initialData,
    validate,
    getFormData,
  };
}
